import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { LoadingPopupComponent } from 'src/app/components/ui/loading-popup/loading-popup.component';
import { DeviceService } from 'src/app/services/devices/device.service';
import { ModalService } from 'src/app/services/modal/modal.service';

interface WifiNetwork {
  ssid: string;
  rssi: number;
}

// v1 ("network" module, cargadores) vs v2 ("wifi" module, ANC+/AstroLum/Datalog+): mismo
// concepto de SSID+contraseña, nombres de campo y de módulo distintos.
const FIELD_MAP = {
  v1: { module: 'network', ssid: 'staEssid', pass: 'staPasswd', scanCommand: 'list_aps', scanKey: 'listAPs', scanParams: undefined },
  v2: { module: 'wifi', ssid: 'ssid_sta', pass: 'pass_sta', scanCommand: 'scan', scanKey: 'networks', scanParams: { localdata: true } },
};

@Component({
    selector: 'app-connectivity',
    templateUrl: './connectivity.component.html',
    standalone: false
})
export class ConnectivityComponent implements OnInit, OnDestroy {

  @Input()
  allSettings = false;

  // Módulos del dispositivo (ver AllSettingsComponent.modules): determina si se usa el
  // módulo "wifi" (v2) o "network" (v1, legacy).
  @Input()
  modules: string[] = [];

  device = '';

  ssid = '';
  originalSsid = '';
  password = '';
  wantsChangePassword = false;
  showPassword = false;

  networks: WifiNetwork[] = [];
  scanning = false;
  scanned = false;
  showDropdown = false;
  private scanTimeout: ReturnType<typeof setTimeout> | null = null;
  private closeDropdownTimeout: ReturnType<typeof setTimeout> | null = null;
  private scanSubscription: Subscription | null = null;

  successMessage = false;
  error = '';
  modalLoad: NgbModalRef;

  constructor(private _aRoute: ActivatedRoute, public _dService: DeviceService, private _modal: ModalService) { }

  private get fields() {
    return this.modules?.includes('wifi') ? FIELD_MAP.v2 : FIELD_MAP.v1;
  }

  ngOnInit(): void {
    this._aRoute.parent?.parent?.params
      .subscribe((params: Params) => this.device = params['serial']);

    this._dService.getModuleInfo(this.device, this.fields.module)
      .subscribe(data => {
        this.ssid = data.cfg?.[this.fields.ssid] || '';
        this.originalSsid = this.ssid;
      });

    this.scanNetworks();
  }

  ngOnDestroy(): void {
    if (this.scanTimeout) clearTimeout(this.scanTimeout);
    if (this.closeDropdownTimeout) clearTimeout(this.closeDropdownTimeout);
    this.scanSubscription?.unsubscribe();
  }

  // Se lanza al abrir la pantalla y a demanda, sin reintentos: en firmware v2 el escaneo
  // bloquea la tarea MQTT y puede desconectar el dispositivo. Un fallo o resultado vacío cae a SSID manual.
  scanNetworks(): void {
    this.scanning = true;
    this.scanned = false;
    if (this.scanTimeout) clearTimeout(this.scanTimeout);
    this.scanSubscription?.unsubscribe();

    const giveUp = () => {
      this.scanSubscription?.unsubscribe();
      this.networks = [];
      this.scanned = true;
      this.stopScanning();
    };
    this.scanTimeout = setTimeout(giveUp, 15000);

    const { module, scanCommand, scanKey, scanParams } = this.fields;
    this.scanSubscription = this._dService.getModuleInfo(this.device, module, scanCommand, scanParams)
      .subscribe({
        next: data => {
          // El endpoint genérico de módulo no es consistente entre pantallas sobre si el
          // resultado de un `level=<command>` va anidado en `stat` o en la raíz.
          const raw = data?.stat?.[scanKey] ?? data?.[scanKey];
          const found = this.normalizeNetworks(raw);
          if (!found.length) return giveUp();
          this.networks = found;
          this.scanned = true;
          this.stopScanning();
          this.showDropdown = true;
        },
        error: giveUp,
      });
  }

  private stopScanning(): void {
    this.scanning = false;
    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
      this.scanTimeout = null;
    }
  }

  // Acepta tanto ["SSID1", ...] (formato antiguo) como [{ssid, rssi}, ...] (formato nuevo);
  // deduplica por SSID quedándose con la de mayor señal, ordenado de mayor a menor.
  private normalizeNetworks(list: any): WifiNetwork[] {
    if (!Array.isArray(list)) return [];
    const bySsid = new Map<string, WifiNetwork>();
    list.forEach(ap => {
      const net: WifiNetwork | null = typeof ap === 'string'
        ? { ssid: ap, rssi: -100 }
        : (ap?.ssid ? { ssid: ap.ssid, rssi: ap.rssi ?? -100 } : null);
      if (!net?.ssid) return;
      const existing = bySsid.get(net.ssid);
      if (!existing || net.rssi > existing.rssi) bySsid.set(net.ssid, net);
    });
    return Array.from(bySsid.values()).sort((a, b) => b.rssi - a.rssi);
  }

  // Término de filtrado del desplegable, independiente de `ssid`: si filtrásemos directamente
  // por `ssid`, el campo ya viene precargado con la red actualmente configurada y el desplegable
  // solo mostraría esa coincidencia nada más abrirlo. Se resetea a vacío al abrir (lista completa)
  // y solo se sincroniza con lo que el usuario escribe después.
  searchTerm = '';

  onSsidChange(value: string): void {
    this.ssid = value;
    this.searchTerm = value;
    this.wantsChangePassword = value !== this.originalSsid;
    this.password = '';
    this.showPassword = false;
  }

  get filteredNetworks(): WifiNetwork[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.networks;
    return this.networks.filter(net => net.ssid.toLowerCase().includes(term));
  }

  selectNetwork(net: WifiNetwork): void {
    this.onSsidChange(net.ssid);
    this.showDropdown = false;
  }

  openDropdown(): void {
    if (this.closeDropdownTimeout) clearTimeout(this.closeDropdownTimeout);
    this.searchTerm = '';
    if (this.networks.length) this.showDropdown = true;
  }

  // mousedown en las opciones (ver template) se dispara antes que este blur, así que el click
  // sobre una red sí llega a registrarse antes de cerrar el desplegable.
  closeDropdownDelayed(): void {
    this.closeDropdownTimeout = setTimeout(() => this.showDropdown = false, 150);
  }

  toggleChangePassword(): void {
    this.wantsChangePassword = !this.wantsChangePassword;
    if (!this.wantsChangePassword) {
      this.password = '';
      this.showPassword = false;
    }
  }

  get ssidChanged(): boolean {
    return this.ssid !== this.originalSsid;
  }

  get passwordRequired(): boolean {
    return this.ssidChanged || this.wantsChangePassword;
  }

  get saveDisabled(): boolean {
    if (!this.ssidChanged && !this.wantsChangePassword) return true;
    return this.passwordRequired && !this.password.trim();
  }

  save(): void {
    this.modalLoad = this._modal.open(LoadingPopupComponent);

    const { module, ssid: ssidField, pass: passField } = this.fields;
    const body: any = { [ssidField]: this.ssid };
    if (this.passwordRequired) body[passField] = this.password;

    this._dService.setModuleCfg(this.device, body, module)
      .subscribe(() => {
        this.modalLoad.close();
        this.originalSsid = this.ssid;
        this.wantsChangePassword = false;
        this.password = '';
        this.successMessage = true;
        setTimeout(() => this.successMessage = false, 10000);
      }, err => {
        console.error(err);
        this.error = err.error?.message || '';
        setTimeout(() => this.error = '', 10000);
        this.modalLoad.close();
      });
  }

  restore(): void {
    this.ssid = this.originalSsid;
    this.password = '';
    this.wantsChangePassword = false;
    this.showPassword = false;
    this.searchTerm = '';
    this.showDropdown = false;
  }

}
